/* @Copyright 2026. Institute for Future Intelligence, Inc. */

// Polymer-relevant torsion parameters. Keyed by element-symbol QUADRUPLE
// (the four atoms in order along the dihedral: 1-2-3-4). Lookup is bidirectional:
// 'C-C-C-C' and 'C-C-C-C' reversed are the same dihedral. Subset of OPLS-AA
// drawn from Jorgensen, Maxwell & Tirado-Rives 1996 (JACS 118, 11225). This is
// a polymer-scoped pack — NOT a full OPLS replacement. Generic torsions
// (anything not in this table) fall back to the engine defaults
// (strength = 1, periodicity = 1).

export interface TorsionParams {
  strength: number; // eV — converted from kcal/mol below
  periodicity: number; // n in V = 0.5 * strength * (1 - cos(n*phi - offset))
  dihedralOffset: number; // radians; π for trans-minimum (sp3 backbones)
}

// 1 kcal/mol ≈ 0.04336 eV. Common sp3 backbone barrier V_3 ≈ 0.65 kcal/mol
// (per-CH bond) gives the ~2.9 kcal/mol gauche-trans barrier when summed
// over the 9 H-C-C-H dihedrals in butane. For our coarser AIMS torsions
// (one per heavy-atom quadruple, no H-X-X-H terms), bump V_3 to capture the
// barrier in the single heavy-atom torsion: ~0.13 eV (3 kcal/mol).
//
// Add entries only for quadruples we've cross-checked against OPLS or
// AMBER GAFF. When in doubt, leave out — fallback is the engine default.
// The values below are trend-correct stand-ins (3-fold barrier with trans
// minimum at phi = π) — the per-quadruple strengths approximate the
// effective heavy-atom barriers that emerge once H-X-X-H terms are
// folded in. Refine against a tabulated OPLS-AA torsion before
// extending this list significantly.
const PI = Math.PI;

// Currently empty. The OPLS-style entries (C-C-C-C, H-C-C-H, etc.) sit at
// their potential MAXIMUM for half the polymer's backbone dihedrals: the
// monomer's rigid intra-unit direction means the chain has an alternating
// pattern of intra-unit and inter-unit bonds; only one parity of backbone
// dihedrals matches the OPLS trans minimum even with a trans-zigzag
// inter-unit layout. Each "wrong-parity" torsion holds ~0.13 eV at rest,
// and a multi-chain bundle dumped tens of eV into KE during the first
// few ps — NVE blew to 10k+ K.
//
// Re-enabling these requires either (a) a builder that also zigzags the
// monomer's internal head→tail direction so BOTH parities land at trans,
// or (b) a relaxation pass (energy minimization) before NVE. Until then
// every torsion falls back to the engine defaults (strength = 1,
// periodicity = 1, equilibrium = the as-built dihedral), so V = 0 at rest
// for every torsion. Persistence-length estimates from MD under this
// regime are qualitative, not quantitative.
export const TORSION_PARAMETERS: Record<string, TorsionParams> = {};
void PI;

/**
 * Build the canonical key 'A-B-C-D' from a quadruple of element symbols.
 */
const buildKey = (elements: [string, string, string, string]): string => {
  return `${elements[0]}-${elements[1]}-${elements[2]}-${elements[3]}`;
};

/**
 * Look up the torsion parameters for a quadruple of element symbols.
 * Falls back to the reversed quadruple ('A-B-C-D' ≡ 'D-C-B-A') if the
 * forward key is not in the table. Returns null when neither key is
 * present — the engine default (strength = 1, periodicity = 1) then
 * applies.
 */
export const lookupTorsionParams = (elements: [string, string, string, string]): TorsionParams | null => {
  const fwd = buildKey(elements);
  if (TORSION_PARAMETERS[fwd]) return TORSION_PARAMETERS[fwd];
  const rev = buildKey([elements[3], elements[2], elements[1], elements[0]]);
  if (TORSION_PARAMETERS[rev]) return TORSION_PARAMETERS[rev];
  return null;
};
